import { useCallback } from "react";
import { Copy, ScanLine, Pin, EyeOffIcon } from "lucide-react";
import {
  SortAscendingIcon,
  SortDescendingIcon,
  FilterIcon,
  TrashIcon,
} from "@heroicons/react/outline";

import { useGridContext } from "../../hooks/useEvaluateGridContext";

import { useColumnDeletion } from "./useColumnDeletion";
import { useColumnDuplication } from "./useColumnDuplication";
import { useColumnSorting } from "./useColumnSorting";
import { useColumnPinning } from "./useColumnPinning";

import { ContextMenuItems, ContextMenuVariant } from "@/components/ui/ContextMenu/types";
import { useSearchOptional } from "@/components/ui/table/Search/search-store";
import { ColumnType, ReportColumn } from "@/types/evaluate";
import { Action, DropdownMenuVariant } from "@/components/ui/DropdownMenu/types";
import { useRbac } from "@/providers/RbacProvider";
import { EntityType } from "@/queries";

interface UseHeaderContextMenuProps {
  column: ReportColumn;
  reportId: number;
  isEditable: boolean;
  showFilterInput: boolean;
  setShowFilterInput: (show: boolean) => void;
}

export const useHeaderContextMenu = ({
  column,
  reportId,
  isEditable,
  showFilterInput,
  setShowFilterInput,
}: UseHeaderContextMenuProps) => {
  const searchStore = useSearchOptional();
  const enabled = searchStore?.enabled ?? false;
  const { gridApiRef } = useGridContext();
  const isDraft = !column.id || column.id === -999;
  const { handleDuplicateColumn } = useColumnDuplication({
    reportId,
  });
  const {
    isDeleteConfirmationOpen,
    setIsDeleteConfirmationOpen,
    handleDeleteColumn,
    handleConfirmDelete,
    handleCancelDelete,
  } = useColumnDeletion({ reportId });
  const { handleSort } = useColumnSorting();
  const { handlePinLeft, handlePinRight, handleUnpin, isColumnPinned } = useColumnPinning();
  const { isEditDisabled } = useRbac();
  const cannotEditBlueprint = isEditDisabled(EntityType.REPORT);
  const handleHideColumn = useCallback(() => {
    gridApiRef?.current?.applyColumnState({
      state: [{ colId: `col_${column.id}`, hide: true }],
    });
  }, [column.id, gridApiRef]);

  const handleShowFilter = useCallback(() => {
    setShowFilterInput(true);
  }, [setShowFilterInput]);

  const contextMenuItems: ContextMenuItems<ReportColumn>[] = [
    {
      id: "duplicate",
      label: "Duplicate",
      icon: Copy,
      onClick: () => handleDuplicateColumn(column),
      getDisabled: () => cannotEditBlueprint,
      tooltipContent: cannotEditBlueprint
        ? "You don't have permission to duplicate this column"
        : undefined,
      hidden: !isEditable || isDraft || column.column_type === ColumnType.DATASET,
    },
    {
      id: "hide",
      label: "Hide",
      icon: EyeOffIcon,
      onClick: () => handleHideColumn(),
      hidden: isDraft,
    },
    {
      id: "auto-size-column",
      label: "Auto size column",
      icon: ScanLine,
      onClick: () => gridApiRef?.current?.autoSizeColumns([`col_${column?.id}`]),
    },
    {
      id: isColumnPinned(column, "left") ? "unpin-left" : "pin-left",
      label: isColumnPinned(column, "left") ? "Unpin left" : "Pin left",
      icon: Pin,
      onClick: () => (isColumnPinned(column, "left") ? handleUnpin(column) : handlePinLeft(column)),
    },
    {
      id: isColumnPinned(column, "right") ? "unpin-right" : "pin-right",
      label: isColumnPinned(column, "right") ? "Unpin right" : "Pin right",
      icon: Pin,
      onClick: () =>
        isColumnPinned(column, "right") ? handleUnpin(column) : handlePinRight(column),
    },
    {
      id: "filter",
      label: "Filter",
      icon: FilterIcon,
      onClick: handleShowFilter,
      hidden: !enabled || isDraft,
    },
    {
      id: "sort-asc",
      label: "Sort ascending",
      icon: SortAscendingIcon,
      onClick: () => handleSort(column, "asc"),
      hidden: !enabled || isDraft,
    },
    {
      id: "sort-desc",
      label: "Sort descending",
      icon: SortDescendingIcon,
      onClick: () => handleSort(column, "desc"),
      hidden: !enabled || isDraft,
    },
    {
      id: "delete",
      label: "Delete",
      icon: TrashIcon,
      onClick: () => handleDeleteColumn(column),
      getDisabled: () => cannotEditBlueprint,
      tooltipContent: cannotEditBlueprint
        ? "You don't have permission to delete this column"
        : undefined,
      variant: ContextMenuVariant.DESTRUCTIVE,
      hidden: !isEditable || isDraft || column.column_type === ColumnType.DATASET,
    },
  ];

  const actions: Action<ReportColumn>[] = [
    {
      id: "duplicate",
      label: "Duplicate",
      icon: <Copy className="h-5 w-5" />,
      onClick: () => handleDuplicateColumn(column),
      getDisabled: () => cannotEditBlueprint,
      tooltipContent: cannotEditBlueprint
        ? "You don't have permission to duplicate this column"
        : undefined,
      shouldShow: isEditable && !isDraft && column.column_type !== ColumnType.DATASET,
    },
    {
      id: "hide",
      label: "Hide",
      icon: EyeOffIcon,
      onClick: () => handleHideColumn(),
      shouldShow: !isDraft,
    },
    {
      id: "auto-size-column",
      label: "Auto size column",
      icon: <ScanLine className="h-5 w-5" />,
      onClick: () => gridApiRef?.current?.autoSizeColumns([`col_${column?.id}`]),
    },
    {
      id: isColumnPinned(column, "left") ? "unpin-left" : "pin-left",
      label: isColumnPinned(column, "left") ? "Unpin left" : "Pin left",
      icon: <Pin className="h-5 w-5" />,
      onClick: () => (isColumnPinned(column, "left") ? handleUnpin(column) : handlePinLeft(column)),
    },
    {
      id: isColumnPinned(column, "right") ? "unpin-right" : "pin-right",
      label: isColumnPinned(column, "right") ? "Unpin right" : "Pin right",
      icon: <Pin className="h-5 w-5" />,
      onClick: () =>
        isColumnPinned(column, "right") ? handleUnpin(column) : handlePinRight(column),
    },
    {
      id: "filter",
      label: "Filter",
      icon: FilterIcon,
      onClick: handleShowFilter,
      shouldShow: enabled && !isDraft,
    },
    {
      id: "sort-asc",
      label: "Sort ascending",
      icon: SortAscendingIcon,
      onClick: () => handleSort(column, "asc"),
      shouldShow: enabled && !isDraft,
    },
    {
      id: "sort-desc",
      label: "Sort descending",
      icon: SortDescendingIcon,
      onClick: () => handleSort(column, "desc"),
      shouldShow: enabled && !isDraft,
    },
    {
      id: "delete",
      label: "Delete",
      icon: TrashIcon,
      onClick: () => handleDeleteColumn(column),
      variant: DropdownMenuVariant.DESTRUCTIVE,
      getDisabled: () => cannotEditBlueprint,
      tooltipContent: cannotEditBlueprint
        ? "You don't have permission to delete this column"
        : undefined,
      shouldShow: isEditable && !isDraft && column.column_type !== ColumnType.DATASET,
    },
  ];

  return {
    contextMenuItems,
    isDeleteConfirmationOpen,
    showFilterInput,
    setShowFilterInput,
    setIsDeleteConfirmationOpen,
    handleConfirmDelete: () => handleConfirmDelete(column),
    handleCancelDelete,
    column,
    actions,
  };
};
